import { useQuery } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/20/solid'
import { ArchiveBoxIcon, CurrencyEuroIcon, TagIcon, CubeIcon } from '@heroicons/react/24/outline'
import { productsApi } from '../../lib/api'

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>()

  const { data: product, isLoading, error } = useQuery({
    queryKey: ['products', id],
    queryFn: () => productsApi.getDetails(id!),
    enabled: !!id,
  })

  if (isLoading) return <div className="p-8">Loading...</div>
  if (error || !product) {
    return (
      <div className="p-8">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900">Product not found</h2>
          <Link to="/products" className="text-brand-600 hover:text-brand-700 mt-2 inline-block">
            Back to Products
          </Link>
        </div>
      </div>
    )
  }

  const formatPrice = (price: string | null, currency: string) => {
    if (!price) return '-'
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(parseFloat(price))
  }

  const formatDate = (date: string | Date | null) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  return (
    <>
      {/* Secondary column - Product info */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <Link
              to="/products"
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to Products
            </Link>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 h-12 w-12 rounded-lg bg-brand-100 flex items-center justify-center">
                <ArchiveBoxIcon className="h-6 w-6 text-brand-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{product.itemCode}</h2>
                <p className="text-sm text-gray-500">{product.itemName}</p>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2">
            {product.valid === 'tYES' ? (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-full bg-green-100 text-green-800">
                <CheckCircleIcon className="h-4 w-4" />
                Active
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-full bg-gray-100 text-gray-600">
                <XCircleIcon className="h-4 w-4" />
                Inactive
              </span>
            )}
            {product.salesItem && (
              <span className="px-3 py-1.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                Sales Item
              </span>
            )}
            {product.purchaseItem && (
              <span className="px-3 py-1.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                Purchase Item
              </span>
            )}
          </div>

          {/* Quick Info */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Info</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Default Price</dt>
                <dd className="font-medium text-gray-900">
                  {formatPrice(product.defaultPrice, product.defaultCurrency)}
                </dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Sales Unit</dt>
                <dd className="font-medium text-gray-900">{product.salesUnit}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Group</dt>
                <dd className="font-medium text-gray-900">{product.itemsGroupName || '-'}</dd>
              </div>
              {product.barCode && (
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-500">Bar Code</dt>
                  <dd className="font-medium text-gray-900">{product.barCode}</dd>
                </div>
              )}
              {product.vatGroup && (
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-500">VAT Group</dt>
                  <dd className="font-medium text-gray-900">{product.vatGroup}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Sync Info */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Sync Status</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Source</dt>
                <dd className="font-medium text-gray-900 capitalize">{product.source}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Status</dt>
                <dd className="font-medium text-gray-900 capitalize">{product.syncStatus}</dd>
              </div>
              {product.lastSyncedAt && (
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-500">Last Synced</dt>
                  <dd className="font-medium text-gray-900">{formatDate(product.lastSyncedAt)}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6">
          {/* Mobile back link */}
          <div className="xl:hidden mb-6">
            <Link
              to="/products"
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to Products
            </Link>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">{product.itemName}</h1>
            {product.foreignName && (
              <p className="mt-1 text-gray-500">{product.foreignName}</p>
            )}
          </div>

          {/* Description */}
          {(product.description || product.shortDescription) && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Description</h2>
              <div className="bg-gray-50 rounded-lg p-4">
                {product.shortDescription && (
                  <p className="text-sm font-medium text-gray-700 mb-2">{product.shortDescription}</p>
                )}
                {product.description && (
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{product.description}</p>
                )}
              </div>
            </div>
          )}

          {/* Product Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {/* Identification */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <TagIcon className="h-5 w-5 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900">Identification</h3>
              </div>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Item Code</dt>
                  <dd className="font-medium text-gray-900">{product.itemCode}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Item Type</dt>
                  <dd className="font-medium text-gray-900 capitalize">{product.itemType.replace('it', '')}</dd>
                </div>
                {product.barCode && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Bar Code</dt>
                    <dd className="font-medium text-gray-900">{product.barCode}</dd>
                  </div>
                )}
                {product.supplierCatalogNo && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Supplier Catalog #</dt>
                    <dd className="font-medium text-gray-900">{product.supplierCatalogNo}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Pricing */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <CurrencyEuroIcon className="h-5 w-5 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900">Pricing</h3>
              </div>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Default Price</dt>
                  <dd className="font-medium text-gray-900">
                    {formatPrice(product.defaultPrice, product.defaultCurrency)}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Currency</dt>
                  <dd className="font-medium text-gray-900">{product.defaultCurrency}</dd>
                </div>
                {product.vatGroup && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">VAT Group</dt>
                    <dd className="font-medium text-gray-900">{product.vatGroup}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Units of Measure */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <CubeIcon className="h-5 w-5 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900">Units of Measure</h3>
              </div>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Sales Unit</dt>
                  <dd className="font-medium text-gray-900">{product.salesUnit}</dd>
                </div>
                {product.salesItemsPerUnit && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Items/Unit</dt>
                    <dd className="font-medium text-gray-900">{product.salesItemsPerUnit}</dd>
                  </div>
                )}
                {product.purchaseUnit && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Purchase Unit</dt>
                    <dd className="font-medium text-gray-900">{product.purchaseUnit}</dd>
                  </div>
                )}
                {product.defaultWarehouse && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Warehouse</dt>
                    <dd className="font-medium text-gray-900">{product.defaultWarehouse}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Price Lists */}
          {product.prices && product.prices.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Price Lists</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price List
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Currency
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Valid From
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Valid To
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {product.prices.map((price) => (
                      <tr key={price.id}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {price.priceListName || `Price List ${price.priceListCode}`}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatPrice(price.price, price.currency)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {price.currency}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(price.validFrom)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(price.validTo)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Weight & Dimensions */}
          {(product.salesUnitWeight || product.salesUnitVolume) && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Weight & Dimensions</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {product.salesUnitWeight && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <dt className="text-xs text-gray-500 mb-1">Weight</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {product.salesUnitWeight} {product.salesWeightUnit || 'kg'}
                    </dd>
                  </div>
                )}
                {product.salesUnitVolume && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <dt className="text-xs text-gray-500 mb-1">Volume</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {product.salesUnitVolume} {product.salesVolumeUnit || 'm³'}
                    </dd>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="border-t pt-6 mt-8">
            <dl className="flex gap-8 text-xs text-gray-500">
              <div>
                <dt className="inline">Created: </dt>
                <dd className="inline">{formatDate(product.createdAt)}</dd>
              </div>
              <div>
                <dt className="inline">Updated: </dt>
                <dd className="inline">{formatDate(product.updatedAt)}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </>
  )
}
